import { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Input, message, Upload,
  Descriptions, Typography, Row, Col, Statistic, Tooltip, Popconfirm, List
} from 'antd';
import {
  ReloadOutlined, CameraOutlined, PoweroffOutlined, SyncOutlined,
  CodeOutlined, DesktopOutlined, HistoryOutlined, InfoCircleOutlined,
  DeleteOutlined, SearchOutlined, UploadOutlined, ClearOutlined
} from '@ant-design/icons';
import { getApiUrl } from '../utils/api';
import { useSimpleStore } from '../stores/simpleStore';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface CtrlDevice {
  mac: string;
  vehicle_number: string;
  reported_vehicle_number: string;
  route_number: string;
  model: string;
  android_version: string;
  bus_nav_version: string;
  ctrl_version: string;
  ip_addresses: string;
  external_ip: string;
  status_text: string;
  health_status: string;
  health_issues: string;
  last_seen: string;
  created_at: string;
  company_id?: number;
  company_name?: string;
}

interface StatusLog { id: number; mac: string; status_text: string; created_at: string; }

const terminalStyle: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 12, background: '#1e1e1e', color: '#d4d4d4',
  padding: 16, borderRadius: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
  maxHeight: 500, overflow: 'auto', lineHeight: 1.5,
};

const parseIps = (v: unknown): Record<string, string> => {
  if (!v) return {};
  if (typeof v === 'object' && !Array.isArray(v)) return v as Record<string, string>;
  try { return JSON.parse(v as string); } catch { return {}; }
};

const parseIssues = (v: unknown): string[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v as string[];
  try { return JSON.parse(v as string); } catch { return []; }
};

const healthColor = (status: string) => {
  if (status === 'critical') return 'red';
  if (status === 'warning') return 'orange';
  return 'green';
};

const healthLabel = (status: string, issues: string[]) => {
  if (status === 'critical') return issues[0] || '심각';
  if (status === 'warning') return issues[0] || '주의';
  return '정상';
};

const CtrlDeviceManagement = () => {
  const { user } = useSimpleStore();
  const [devices, setDevices] = useState<CtrlDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedDevice, setSelectedDevice] = useState<CtrlDevice | null>(null);
  const [statusVisible, setStatusVisible] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLogs, setHistoryLogs] = useState<StatusLog[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
  const [shellVisible, setShellVisible] = useState(false);
  const [shellCommand, setShellCommand] = useState('');
  const [shellResult, setShellResult] = useState('');
  const [shellLoading, setShellLoading] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [screenshotVisible, setScreenshotVisible] = useState(false);
  const [screenshotDisplay, setScreenshotDisplay] = useState<'main' | 'hdmi'>('main');
  const [apkVisible, setApkVisible] = useState(false);
  const [apkUploading, setApkUploading] = useState(false);
  const [apkPackage, setApkPackage] = useState('kr.forworld.bus_nav');
  const [clearConfirmVisible, setClearConfirmVisible] = useState(false);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const companyFilter = user?.role === 'operator' && user?.company_id ? `?company_id=${user.company_id}` : '';
      const res = await fetch(getApiUrl(`/api/ctrl/devices${companyFilter}`));
      const data = await res.json();
      if (data.success) setDevices(data.data.devices || []);
    } catch { message.error('단말 목록 조회 실패'); }
    finally { setLoading(false); }
  }, [user?.role, user?.company_id]);

  useEffect(() => {
    loadDevices();
    const interval = setInterval(loadDevices, 30000);
    return () => clearInterval(interval);
  }, [loadDevices]);

  const sendCommand = async (mac: string, command: string, params: Record<string, string> = {}) => {
    try {
      const res = await fetch(getApiUrl('/api/ctrl/commands'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mac, command, params }),
      });
      const data = await res.json();
      if (data.success) {
        message.loading({ content: `${command} 실행 중...`, key: command, duration: 0 });
        waitForResult(data.data.command_id, command);
        return data.data.command_id;
      }
      message.error('명령 전송 실패');
    } catch { message.error('명령 전송 오류'); }
    return null;
  };

  // 명령 결과 폴링 후 Modal로 결과 표시
  const waitForResult = (commandId: string, label: string) => {
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(getApiUrl(`/api/ctrl/commands/result/${commandId}`));
        const data = await res.json();
        if (data.success && data.data.status !== 'pending') {
          clearInterval(poll);
          message.destroy(label);
          const output = data.data.output || '(출력 없음)';
          if (data.data.success) {
            Modal.success({ title: `${label} 완료`, content: <pre style={{ maxHeight: 200, overflow: 'auto', fontSize: 12 }}>{output}</pre> });
          } else {
            Modal.error({ title: `${label} 실패`, content: <pre style={{ maxHeight: 200, overflow: 'auto', fontSize: 12 }}>{output}</pre> });
          }
        }
      } catch { /* */ }
      if (attempts >= 60) {
        clearInterval(poll);
        message.destroy(label);
        Modal.warning({ title: `${label}`, content: '60초 내 응답이 없습니다. 단말이 오프라인이거나 처리 중입니다.' });
      }
    }, 1000);
  };

  const deleteDevice = async (mac: string) => {
    try {
      const res = await fetch(getApiUrl(`/api/ctrl/devices?mac=${encodeURIComponent(mac)}`), { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { message.success('삭제 완료'); loadDevices(); }
      else message.error('삭제 실패');
    } catch { message.error('삭제 오류'); }
  };

  // 차량번호 수동 입력/수정 (단말이 빈 값을 보내도 서버에서 유지됨)
  const saveVehicleNumber = async (mac: string, vehicle_number: string) => {
    try {
      const res = await fetch(getApiUrl(`/api/ctrl/devices/${encodeURIComponent(mac)}/vehicle`), {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vehicle_number }),
      });
      const data = await res.json();
      if (data.success) { message.success('차량번호 저장'); loadDevices(); }
      else message.error('차량번호 저장 실패');
    } catch { message.error('차량번호 저장 오류'); }
  };

  const handleScreenshot = async (device: CtrlDevice, display: 'main' | 'hdmi') => {
    const cmdName = display === 'hdmi' ? 'screenshot_hdmi' : 'screenshot';
    message.loading({ content: '스크린샷 촬영 중...', key: 'ss', duration: 0 });

    // 명령 전송
    try {
      const res = await fetch(getApiUrl('/api/ctrl/commands'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mac: device.mac, command: cmdName, params: {} }),
      });
      const data = await res.json();
      if (!data.success) { message.destroy('ss'); message.error('명령 전송 실패'); return; }

      const commandId = data.data.command_id;

      // 결과 대기 (최대 60초)
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const r = await fetch(getApiUrl(`/api/ctrl/commands/result/${commandId}`));
          const d = await r.json();
          if (d.success && d.data.status !== 'pending') {
            clearInterval(poll);
            message.destroy('ss');
            if (d.data.success) {
              setScreenshotUrl(getApiUrl(`/api/ctrl/screenshot/${device.mac}?display=${display}&t=${Date.now()}`));
              setScreenshotDisplay(display);
              setScreenshotVisible(true);
            } else {
              message.error(`스크린샷 실패: ${d.data.output}`);
            }
          }
        } catch { /* */ }
        if (attempts >= 60) { clearInterval(poll); message.destroy('ss'); message.warning('스크린샷 응답 대기 초과'); }
      }, 1000);
    } catch { message.destroy('ss'); message.error('명령 전송 오류'); }
  };

  const handleShellExec = async () => {
    if (!selectedDevice || !shellCommand.trim()) return;
    setShellLoading(true); setShellResult('');
    const commandId = await sendCommand(selectedDevice.mac, 'shell', { cmd: shellCommand });
    if (!commandId) { setShellLoading(false); return; }
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(getApiUrl(`/api/ctrl/commands/result/${commandId}`));
        const data = await res.json();
        if (data.success && data.data.status !== 'pending') {
          clearInterval(poll); setShellResult(data.data.output || '(출력 없음)'); setShellLoading(false);
        }
      } catch { /* */ }
      if (attempts >= 60) { clearInterval(poll); setShellResult('타임아웃'); setShellLoading(false); }
    }, 1000);
  };

  const loadHistory = async (mac: string) => {
    setHistoryLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/ctrl/status-history/${mac}?limit=20`));
      const data = await res.json();
      if (data.success) setHistoryLogs(data.data.logs || []);
    } catch { message.error('이력 조회 실패'); }
    finally { setHistoryLoading(false); }
  };

  // APK 업로드 후 설치 명령 전송
  const handleApkInstall = async (file: File) => {
    if (!selectedDevice) return;
    setApkUploading(true);
    try {
      const formData = new FormData();
      formData.append('apk', file);
      const uploadRes = await fetch(getApiUrl('/api/ctrl/apk/upload'), { method: 'POST', body: formData });
      const uploadData = await uploadRes.json();
      if (!uploadData.success) { message.error('APK 업로드 실패'); return; }

      const downloadUrl = `${getApiUrl(uploadData.data.url)}`;
      await sendCommand(selectedDevice.mac, 'install_apk', { url: downloadUrl, package: apkPackage });
      message.info('APK 설치 명령 전송 완료 (설치에 시간이 소요될 수 있습니다)');
      setApkVisible(false);
    } catch { message.error('APK 설치 오류'); }
    finally { setApkUploading(false); }
  };

  const isOnline = (lastSeen: string) => {
    if (!lastSeen) return false;
    return Date.now() - new Date(lastSeen).getTime() < 720000;
  };

  const filteredDevices = devices.filter(d => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return d.mac.toLowerCase().includes(q)
      || (d.vehicle_number || '').toLowerCase().includes(q)
      || (d.route_number || '').toLowerCase().includes(q)
      || (d.company_name || '').toLowerCase().includes(q)
      || (d.external_ip || '').includes(q)
      || JSON.stringify(d.ip_addresses || '').includes(q);
  });

  const columns = [
    {
      title: '상태', dataIndex: 'health_status', key: 'status', width: 100,
      render: (_: string, r: CtrlDevice) => {
        const online = isOnline(r.last_seen);
        if (!online) return <Tag color="default">오프라인</Tag>;
        const hs = r.health_status || 'ok';
        const issues = parseIssues(r.health_issues);
        return <Tooltip title={issues.length > 0 ? issues.join(', ') : '정상'}>
          <Tag color={healthColor(hs)}>{healthLabel(hs, issues)}</Tag>
        </Tooltip>;
      },
    },
    {
      title: 'MAC', dataIndex: 'mac', key: 'mac', width: 155,
      render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text>,
    },
    { title: '운수사', dataIndex: 'company_name', key: 'company', width: 70, render: (v: string) => v || '-' },
    {
      title: '차량(수동)', dataIndex: 'vehicle_number', key: 'vehicle', width: 110,
      render: (v: string, record: CtrlDevice) => (
        <Text editable={{ text: v || '', tooltip: '클릭하여 차량번호 입력 (수동 전용 — 단말 자동 보고로 덮어써지지 않음)', onChange: (val) => saveVehicleNumber(record.mac, val.trim()) }}>
          {v || '—'}
        </Text>
      ),
    },
    {
      title: '단말보고', dataIndex: 'reported_vehicle_number', key: 'reported_vehicle', width: 100,
      render: (v: string, r: CtrlDevice) => {
        const rep = v || '';
        if (!rep) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        const manual = r.vehicle_number || '';
        const mismatch = manual !== '' && rep !== manual;
        return (
          <Tooltip title={mismatch
            ? '단말이 자동 보고한 값이 수동 입력과 다릅니다. 단말의 config.txt(VEHICLE_NUMBER) 또는 OBE carno.txt를 실제 번호로 교정하세요.'
            : '단말(bus_ctrl)이 자동 보고한 차량번호'}>
            <Text style={{ fontSize: 12, color: mismatch ? '#fa8c16' : '#888' }}>
              {rep}{mismatch ? ' ⚠️' : ''}
            </Text>
          </Tooltip>
        );
      },
    },
    { title: '노선', dataIndex: 'route_number', key: 'route', width: 80 },
    {
      title: 'IP', key: 'ip', width: 230,
      render: (_: unknown, r: CtrlDevice) => {
        const ips = parseIps(r.ip_addresses);
        const ext = r.external_ip || ips['external'] || '';
        return <Space direction="vertical" size={0}>
          {Object.entries(ips).filter(([k]) => k !== 'external').map(([k, ip]) => (
            <Text key={k} style={{ fontSize: 11 }}>{k}: {ip}</Text>
          ))}
          {ext && <Text style={{ fontSize: 11, color: '#1890ff' }}>외부: {ext}</Text>}
        </Space>;
      },
    },
    { title: 'bus_nav', dataIndex: 'bus_nav_version', key: 'nav', width: 100, ellipsis: true },
    {
      title: '마지막 통신', dataIndex: 'last_seen', key: 'seen', width: 135,
      render: (v: string) => v ? new Date(v).toLocaleString('ko-KR', { hour12: false }) : '-',
    },
    {
      title: '관리', key: 'action', width: 420, fixed: 'right' as const,
      render: (_: unknown, r: CtrlDevice) => (
        <Space size={4} wrap>
          <Button size="small" icon={<InfoCircleOutlined />} onClick={() => { setSelectedDevice(r); setStatusVisible(true); }}>상태</Button>
          <Button size="small" icon={<HistoryOutlined />} onClick={() => { setSelectedDevice(r); setHistoryVisible(true); setExpandedLogId(null); loadHistory(r.mac); }}>이력</Button>
          <Button size="small" icon={<CameraOutlined />} onClick={() => handleScreenshot(r, 'main')}>메인</Button>
          <Button size="small" icon={<DesktopOutlined />} onClick={() => handleScreenshot(r, 'hdmi')}>HDMI</Button>
          <Button size="small" icon={<CodeOutlined />} onClick={() => { setSelectedDevice(r); setShellVisible(true); setShellResult(''); setShellCommand(''); }}>셸</Button>
          <Button size="small" icon={<UploadOutlined />} onClick={() => { setSelectedDevice(r); setApkVisible(true); }}>APK</Button>
          <Popconfirm title="bus_nav 재시작?" onConfirm={() => sendCommand(r.mac, 'restart_app')}>
            <Button size="small" icon={<SyncOutlined />}>재시작</Button>
          </Popconfirm>
          <Button size="small" danger icon={<ClearOutlined />} onClick={() => { setSelectedDevice(r); setClearConfirmVisible(true); }}>데이터삭제</Button>
          <Popconfirm title="단말 재부팅?" okType="danger" onConfirm={() => sendCommand(r.mac, 'reboot')}>
            <Button size="small" danger icon={<PoweroffOutlined />}>재부팅</Button>
          </Popconfirm>
          <Popconfirm title="목록에서 삭제?" okType="danger" onConfirm={() => deleteDevice(r.mac)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const onlineCount = filteredDevices.filter(d => isOnline(d.last_seen)).length;
  const warningCount = filteredDevices.filter(d => isOnline(d.last_seen) && d.health_status === 'warning').length;
  const criticalCount = filteredDevices.filter(d => isOnline(d.last_seen) && d.health_status === 'critical').length;

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>단말기 관리</Title>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}><Card size="small"><Statistic title="전체" value={filteredDevices.length} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="정상" value={onlineCount - warningCount - criticalCount} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="주의" value={warningCount} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="심각" value={criticalCount} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={5}>
          <Input placeholder="MAC, 차량, 노선, IP 검색" prefix={<SearchOutlined />}
            value={searchText} onChange={e => setSearchText(e.target.value)} allowClear />
        </Col>
        <Col span={3}><Button icon={<ReloadOutlined />} onClick={loadDevices} loading={loading} block>새로고침</Button></Col>
      </Row>

      <Card>
        <Table dataSource={filteredDevices} columns={columns} rowKey="mac" loading={loading}
          size="small" pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `${t}대` }}
          scroll={{ x: 1500 }} />
      </Card>

      {/* 상태 모달 */}
      <Modal title={`단말 상태 — ${selectedDevice?.vehicle_number || selectedDevice?.mac}`}
        open={statusVisible} onCancel={() => setStatusVisible(false)} footer={null} width={900}>
        {selectedDevice && <>
          <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="MAC">{selectedDevice.mac}</Descriptions.Item>
            <Descriptions.Item label="운수사">{selectedDevice.company_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="차량번호(수동)">{selectedDevice.vehicle_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="단말보고 차량번호">{selectedDevice.reported_vehicle_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="노선">{selectedDevice.route_number}</Descriptions.Item>
            <Descriptions.Item label="Android">{selectedDevice.android_version}</Descriptions.Item>
            <Descriptions.Item label="bus_nav">{selectedDevice.bus_nav_version}</Descriptions.Item>
            <Descriptions.Item label="ctrl">{selectedDevice.ctrl_version}</Descriptions.Item>
            <Descriptions.Item label="건강">
              <Tag color={healthColor(selectedDevice.health_status || 'ok')}>
                {healthLabel(selectedDevice.health_status || 'ok', parseIssues(selectedDevice.health_issues))}
              </Tag>
              {parseIssues(selectedDevice.health_issues).map((issue, i) => <Tag key={i} color="orange" style={{marginTop:4}}>{issue}</Tag>)}
            </Descriptions.Item>
          </Descriptions>
          <div style={terminalStyle}>{selectedDevice.status_text || '(상태 정보 없음)'}</div>
        </>}
      </Modal>

      {/* 이력 모달 */}
      <Modal title={`상태 이력 — ${selectedDevice?.vehicle_number || selectedDevice?.mac}`}
        open={historyVisible} onCancel={() => setHistoryVisible(false)} footer={null} width={900}>
        <List loading={historyLoading} dataSource={historyLogs} locale={{ emptyText: '이력 없음' }}
          renderItem={(log: StatusLog) => (
            <List.Item style={{ display: 'block', padding: '8px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}>
                <Text strong>{new Date(log.created_at).toLocaleString('ko-KR', { hour12: false })}</Text>
                <Text type="secondary">{expandedLogId === log.id ? '접기' : '펼치기'}</Text>
              </div>
              {expandedLogId === log.id && <div style={{ ...terminalStyle, marginTop: 8, maxHeight: 300 }}>{log.status_text}</div>}
            </List.Item>
          )} />
      </Modal>

      {/* 셸 모달 */}
      <Modal title={`셸 — ${selectedDevice?.vehicle_number || selectedDevice?.mac}`}
        open={shellVisible} onCancel={() => setShellVisible(false)} footer={null} width={700}>
        <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
          <Input placeholder="셸 명령" value={shellCommand}
            onChange={e => setShellCommand(e.target.value)} onPressEnter={handleShellExec} />
          <Button type="primary" loading={shellLoading} onClick={handleShellExec}>실행</Button>
        </Space.Compact>
        <TextArea value={shellResult} readOnly rows={15}
          style={{ fontFamily: 'monospace', fontSize: 12, background: '#1e1e1e', color: '#d4d4d4' }}
          placeholder="결과가 여기에 표시됩니다..." />
      </Modal>

      {/* APK 설치 모달 */}
      <Modal title={`APK 설치 — ${selectedDevice?.vehicle_number || selectedDevice?.mac}`}
        open={apkVisible} onCancel={() => setApkVisible(false)} footer={null} width={500}>
        <div style={{ marginBottom: 12 }}>
          <Text>설치 후 실행할 패키지명:</Text>
          <Input value={apkPackage} onChange={e => setApkPackage(e.target.value)}
            placeholder="kr.forworld.bus_nav" style={{ marginTop: 4 }} />
        </div>
        <Upload.Dragger
          accept=".apk"
          showUploadList={false}
          beforeUpload={(file) => { handleApkInstall(file); return false; }}
          disabled={apkUploading}
        >
          <p className="ant-upload-drag-icon"><UploadOutlined style={{ fontSize: 32 }} /></p>
          <p>{apkUploading ? '업로드 중...' : 'APK 파일을 드래그하거나 클릭하여 선택'}</p>
        </Upload.Dragger>
      </Modal>

      {/* 스크린샷 모달 */}
      <Modal title={`스크린샷 (${screenshotDisplay})`}
        open={screenshotVisible} onCancel={() => setScreenshotVisible(false)} footer={null} width={1000}>
        {screenshotUrl && <img src={screenshotUrl} alt="screenshot" style={{ width: '100%' }}
          onError={() => message.warning('스크린샷을 불러올 수 없습니다.')} />}
      </Modal>

      {/* 데이터 삭제 확인 모달 */}
      <Modal title="데이터 삭제" open={clearConfirmVisible}
        okText="삭제 후 재시작" okType="danger" cancelText="취소"
        onOk={() => { setClearConfirmVisible(false); if (selectedDevice) sendCommand(selectedDevice.mac, 'clear_bus_nav_data'); }}
        onCancel={() => setClearConfirmVisible(false)}>
        <p>bus_nav 데이터를 모두 삭제하고 앱이 재시작됩니다.</p>
        <p>진행하시겠습니까?</p>
      </Modal>
    </div>
  );
};

export default CtrlDeviceManagement;
